import { useEffect, useState, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth, canAccess, ROLE_HOME } from '@/context/AuthContext';
import { seedDatabase, indexedDBAvailable } from '@/db';
import { attachDexieHooks, initFirebaseSync, refreshDeviceNicknameCache } from '@/lib/firebaseSync';
import { startChatbotBridge } from '@/lib/chatbotBridge';
import ErrorBoundary from '@/components/ErrorBoundary';
import MenuErrorBoundary from '@/components/MenuErrorBoundary';
import Sidebar from '@/components/Sidebar';
import LoginPage from '@/pages/LoginPage';
import AdminHomePage from '@/pages/AdminHomePage';
import GarcomPage from '@/pages/GarcomPage';
import CaixaPage from '@/pages/CaixaPage';
import EntregadorPage from '@/pages/EntregadorPage';
import StockPage from '@/pages/StockPage';
import ScalePage from '@/pages/ScalePage';
import PrintPage from '@/pages/PrintPage';
import ReportsPage from '@/pages/ReportsPage';
import KDSPage from '@/pages/KDSPage';
import DeliveryPage from '@/pages/DeliveryPage';
import DeliveryAdminPage from '@/pages/DeliveryAdminPage';
import MenuAdminPage from '@/pages/MenuAdminPage';
import MenuAnalyticsPage from '@/pages/MenuAnalyticsPage';
import LogisticaPage from '@/pages/LogisticaPage';
import EquipePage from '@/pages/EquipePage';
import TrackingPage from '@/pages/TrackingPage';
import SettingsPage from '@/pages/SettingsPage';
import CashDrawerHistoryPage from '@/pages/CashDrawerHistoryPage';
import TrainingBanner from '@/components/TrainingBanner';
import ShiftWidget from '@/components/ShiftWidget';
import AdminPinDialog from '@/components/AdminPinDialog';
import ZapiMonitorPage from '@/pages/ZapiMonitorPage';
import GhostMenuCleanerPage from '@/pages/GhostMenuCleanerPage';
import DevicesPage from '@/pages/DevicesPage';
import '@/App.css';

function ProtectedRoute({ children, path }) {
  const { operator, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <img
          data-testid="splash-logo"
          src="/logo-transparente.webp"
          alt="Kaven Restaurante"
          className="block object-contain w-full animate-in fade-in duration-[800ms]"
          style={{
            maxWidth: '280px',
            filter:
              'drop-shadow(0 4px 8px rgba(15, 23, 42, 0.12)) drop-shadow(0 2px 4px rgba(15, 23, 42, 0.08))',
          }}
        />
      </div>
    );
  }
  if (!operator) return <Navigate to="/login" replace />;
  // Fase 7: Role-based guard
  if (path && !canAccess(operator.role, path)) {
    return <Navigate to={ROLE_HOME[operator.role] || '/login'} replace />;
  }
  return children;
}

function AppLayout({ children }) {
  // v7.87 — Limpeza automática diária (7 dias) nos dispositivos da equipe.
  useEffect(() => {
    const t = setTimeout(() => {
      import('@/lib/autoCleanup')
        .then(({ runDailyCleanup }) => runDailyCleanup())
        .catch(() => {});
    }, 15000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <TrainingBanner />
      <Sidebar />
      <main className="md:ml-[240px] min-h-screen pt-14 pb-16 md:pt-0 md:pb-0">{children}</main>
      <ShiftWidget />
    </div>
  );
}

function HomeRedirect() {
  const { operator } = useAuth();
  if (!operator) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[operator.role] || '/admin'} replace />;
}

function AppRoutes() {
  const { operator } = useAuth();
  const afterLoginTarget = operator ? ROLE_HOME[operator.role] || '/admin' : '/login';

  return (
    <Routes>
      <Route path="/login" element={operator ? <Navigate to={afterLoginTarget} replace /> : <LoginPage />} />

      {/* Home redirect based on role */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Admin routes (role: admin only) */}
      <Route path="/admin" element={<ProtectedRoute path="/admin"><AppLayout><AdminHomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/zapi-monitor" element={<ProtectedRoute path="/admin/zapi-monitor"><AppLayout><ZapiMonitorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/limpeza-fantasma" element={<ProtectedRoute path="/admin/limpeza-fantasma"><AppLayout><GhostMenuCleanerPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/dispositivos" element={<ProtectedRoute path="/admin/dispositivos"><AppLayout><DevicesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/estoque" element={<ProtectedRoute path="/estoque"><AppLayout><StockPage /></AppLayout></ProtectedRoute>} />
      <Route path="/balanca" element={<ProtectedRoute path="/balanca"><AppLayout><ScalePage /></AppLayout></ProtectedRoute>} />
      <Route path="/impressao" element={<ProtectedRoute path="/impressao"><AppLayout><PrintPage /></AppLayout></ProtectedRoute>} />
      <Route path="/delivery-admin" element={<ProtectedRoute path="/delivery-admin"><AppLayout><DeliveryAdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/cardapio" element={<ProtectedRoute path="/cardapio"><AppLayout><MenuErrorBoundary><MenuAdminPage /></MenuErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/cardapio/analytics" element={<ProtectedRoute path="/cardapio"><AppLayout><MenuErrorBoundary><MenuAnalyticsPage /></MenuErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/logistica" element={<ProtectedRoute path="/logistica"><AppLayout><LogisticaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/equipe" element={<ProtectedRoute path="/equipe"><AppLayout><EquipePage /></AppLayout></ProtectedRoute>} />
      <Route path="/config" element={<ProtectedRoute path="/config"><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />

      {/* Role-segmented routes */}
      <Route path="/garcom" element={<ProtectedRoute path="/garcom"><AppLayout><GarcomPage /></AppLayout></ProtectedRoute>} />
      <Route path="/caixa" element={<ProtectedRoute path="/caixa"><AppLayout><CaixaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/entregador" element={<ProtectedRoute path="/entregador"><AppLayout><EntregadorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/cozinha" element={<ProtectedRoute path="/cozinha"><AppLayout><KDSPage /></AppLayout></ProtectedRoute>} />
      <Route path="/relatorios" element={<ProtectedRoute path="/relatorios"><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/historico-caixa" element={<ProtectedRoute path="/historico-caixa"><AppLayout><CashDrawerHistoryPage /></AppLayout></ProtectedRoute>} />

      {/* Public routes (customer facing) */}
      <Route path="/delivery" element={<MenuErrorBoundary><DeliveryPage /></MenuErrorBoundary>} />
      <Route path="/rastreio/:id" element={<TrackingPage />} />

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function AppInner() {
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Hotfix v7.2 — após 5s na splash, oferecer botão de auto-recuperação
    // pro operador final. Caso o boot trave de verdade (Dexie corrompido,
    // listener em loop, etc), ele pode resetar o banco local e recarregar
    // sem precisar suporte. Migrations idempotentes refazem tudo no boot
    // seguinte; Firebase re-baixa snapshot dos outros devices.
    const recoveryTimer = setTimeout(() => {
      if (!cancelled) setShowRecovery(true);
    }, 5000);
    (async () => {
      // v7.15 — iOS Safari Private Mode: indexedDB existe mas QUALQUER
      // operação falha. Detectamos cedo e seguimos em "modo degradado"
      // (UI funciona, sem persistência). Sem essa proteção, o seedDatabase
      // travaria a splash em Safari/iPhone em modo privado.
      if (!indexedDBAvailable) {
        console.warn('[BOOT] IndexedDB indisponível (Safari Private Mode?). Modo degradado: UI sem persistência.');
        if (!cancelled) {
          setBootError('Banco local indisponível neste navegador (modo privado?). O app vai funcionar com limitações até você sair do modo anônimo.');
          setReady(true);
        }
        return;
      }
      // Hotfix v6.3 — Boot rápido: só ESPERA o que é estritamente local.
      // O Dexie tem que estar pronto pra UI renderizar, mas o Firebase
      // (auth + snapshot + listeners) pode bootar em BACKGROUND sem
      // bloquear o splash. Isso elimina o "29s travado" do iPhone.
      try {
        // v7.48 — Timeout reduzido de 8s → 3s. A UI é offline-first; libera
        // mais cedo é seguro porque migrations são idempotentes e completam
        // no próximo boot ou em background. Combinado com bulkPut config
        // (~25 transações IDB → 2), o seed médio passa pra <500ms.
        // v7.64 — Aumentado 3s → 5s: devices lentos estouravam o timeout
        // antes do seed/migrations concluírem, deixando o Dexie incompleto.
        await Promise.race([
          seedDatabase(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('seedDatabase: timeout 5s — liberando UI')), 5000)),
        ]);
      } catch (err) {
        console.error('[BOOT] seedDatabase falhou ou estourou timeout:', err);
        if (!cancelled) setBootError(`Banco local: ${err?.message || err}`);
      }
      try {
        attachDexieHooks();
      } catch (err) {
        console.error('[BOOT] attachDexieHooks falhou:', err);
      }
      // 🚀 UI LIBERADA AGORA. Operador pode logar, criar comandas e atender
      // clientes — tudo offline-first via Dexie. Os pushes vão pra fila
      // pendingWrites e drenam quando o Firebase autenticar.
      if (!cancelled) setReady(true);

      // Firebase em background — não bloqueia a UI.
      Promise.resolve().then(async () => {
        try {
          await refreshDeviceNicknameCache();
        } catch (err) {
          console.warn('[BOOT-BG] refreshDeviceNicknameCache:', err?.message || err);
        }
        try {
          await initFirebaseSync();
        } catch (err) {
          console.warn('[BOOT-BG] initFirebaseSync falhou (modo local):', err?.message || err);
        }
        // Chatbot bridge — consome pedidos vindos do WhatsApp pelo backend.
        // Roda apenas em background, sem impacto na UI.
        try {
          startChatbotBridge();
        } catch (err) {
          console.warn('[BOOT-BG] startChatbotBridge:', err?.message || err);
        }
        // v7.99 — Version Guard: mantém tablets 24/7 no bundle mais novo
        // após redeploys (auto-update seguro, 1x por versão, sem loops).
        try {
          const { startVersionGuard } = await import('@/lib/versionGuard');
          startVersionGuard();
        } catch (err) {
          console.warn('[BOOT-BG] startVersionGuard:', err?.message || err);
        }
      });
    })();

    // Captura erros assíncronos não-tratados (promises rejeitadas) para
    // evitar que o console fique cheio e impeçam debug. Toasts ficam ON.
    const onRejection = (ev) => {
      // eslint-disable-next-line no-console
      console.error('[unhandledrejection]', ev?.reason);
    };
    const onError = (ev) => {
      // eslint-disable-next-line no-console
      console.error('[window.onerror]', ev?.error || ev?.message);
    };
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError);
    // v7.14 — Listener de reset cross-device (sem reload automático).
    // Mostra toast e oferece botão para o operador decidir quando recarregar.
    const onResetDetected = (ev) => {
      // eslint-disable-next-line no-console
      console.log('[App] Reset Total detectado:', ev?.detail);
      try {
        toast('Banco resetado em outro dispositivo. Recarregue quando puder.', {
          duration: 12000,
          action: {
            label: 'Recarregar agora',
            onClick: () => window.location.reload(),
          },
        });
      } catch {}
    };
    window.addEventListener('kaven:resetDetected', onResetDetected);
    // v7.99 — Nova versão publicada mas auto-update não aplicável agora
    // (rota de cliente, aparelho em uso ou já recarregado 1x): toast manual.
    const onNewVersion = () => {
      try {
        toast('Nova versão do Kaven disponível.', {
          duration: 20000,
          action: {
            label: 'Atualizar agora',
            onClick: () => window.location.reload(),
          },
        });
      } catch {}
    };
    window.addEventListener('kaven:newVersion', onNewVersion);
    return () => {
      window.removeEventListener('kaven:newVersion', onNewVersion);
      cancelled = true;
      clearTimeout(recoveryTimer);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onError);
      window.removeEventListener('kaven:resetDetected', onResetDetected);
    };
  }, []);

  const handleRecovery = async () => {
    if (recovering) return;
    setRecovering(true);
    // v7.48 — Recovery NÃO-DESTRUTIVA. Antes chamava `resetDatabase()` que
    // DELETAVA o IndexedDB inteiro — piorando a situação ao forçar todas as
    // migrations a rodarem de novo do zero E regenerar o deviceGroup
    // (orfanizando dados no Firebase). Agora, só limpa o cache de seedPromise
    // e re-tenta `seedDatabase()`, que é idempotente: dados existentes ficam
    // intactos, migrations já marcadas como aplicadas pulam, e o reload
    // refaz o boot com timeout fresh de 3s.
    try {
      // Importa de novo pra acessar `seedPromise` interno via fresh call
      // (seedDatabase já reseta seedPromise no catch — v7.47).
      await seedDatabase();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[RECOVERY] re-seed falhou (vai recarregar mesmo assim):', err?.message || err);
    }
    // Reload sempre — limpa qualquer estado React pendente. Dados Dexie
    // PRESERVADOS (não chamamos resetDatabase). O próximo boot deve ser
    // muito mais rápido (caches Dexie/Firebase quentes).
    window.location.reload();
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-8 gap-6">
        <img
          data-testid="splash-logo"
          src="/logo-transparente.webp"
          alt="Kaven Restaurante"
          className="block object-contain w-full animate-in fade-in duration-[800ms]"
          style={{
            maxWidth: '280px',
            filter:
              'drop-shadow(0 4px 8px rgba(15, 23, 42, 0.12)) drop-shadow(0 2px 4px rgba(15, 23, 42, 0.08))',
          }}
        />
        {/* Spinner animado: feedback visual durante o seedDatabase (Dexie). */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-emerald-600 animate-spin" />
          <div
            className="text-slate-400 text-xs uppercase tracking-[0.3em] font-bold"
            data-testid="splash-loading-text"
          >
            Carregando
          </div>
        </div>
        {bootError && (
          <div className="max-w-sm bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-[11px] text-amber-800 font-mono break-all">
            {bootError}
          </div>
        )}
        {showRecovery && (
          <button
            data-testid="splash-recovery-btn"
            type="button"
            onClick={handleRecovery}
            disabled={recovering}
            className="mt-4 max-w-sm w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:border-slate-900 hover:text-slate-900 transition-all shadow-sm disabled:opacity-60 disabled:cursor-wait animate-in fade-in duration-500"
          >
            {recovering ? 'Limpando cache…' : 'Demorando para carregar? Clique aqui para limpar o cache e reiniciar'}
          </button>
        )}
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppRoutes />
      <AdminPinDialog />
      <ToasterBoundary>
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'font-body',
            style: { border: '1px solid hsl(214 20% 91%)', borderRadius: '12px' },
          }}
        />
      </ToasterBoundary>
    </BrowserRouter>
  );
}

/**
 * v7.90 — Boundary exclusivo do Toaster (sonner). O insertBefore race do
 * toast em commit concorrente NÃO pode borbulhar pro ErrorBoundary global
 * (que remonta a árvore inteira e derrubava estados em memória). Aqui o
 * toast só é silenciado por um instante e volta sozinho.
 */
class ToasterBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.warn('[ToasterBoundary] toast crash isolado (auto-recupera):', error?.message);
    setTimeout(() => this.setState({ failed: false }), 150);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * v7.44 — ErrorBoundary movido para envolver TODO o App (inclusive a fase
 * de boot/splash). Antes ficava só ao redor do Router, então qualquer
 * exception lançada dentro do useEffect inicial (seedDatabase, attachDexieHooks,
 * indexedDB indisponível em race condition) produzia tela branca. Agora
 * o boundary captura e o ErrorBoundary mostra o fallback amigável com
 * botão "Recarregar".
 */
function App() {
  return (
    // v7.90 — AuthProvider FORA do ErrorBoundary: o auto-recovery de erros
    // DOM não-fatais remonta a subárvore, e o provider precisa sobreviver
    // pra não perder o operador logado nem o PIN da sessão (aba Equipe).
    <AuthProvider>
      <ErrorBoundary>
        <AppInner />
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
